import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import CircularProgress from '@material-ui/core/CircularProgress';

const microsoftClientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
const layout = window.$environment?.CURRENT_LAYOUT;
const serviceProvider = window.$environment?.SERVICE_PROVIDER;


const MicrosoftLoginButton = ({ history, fetchMicrosoftLogin, i18n, Styled, webAppConfigurations, hasMicrosoftLoginApiStatus, redirectedFromFleetops, redirectedUrl }) => {
    const [isButtonClicked, setIsButtonClicked] = useState(false);
    const [msalReady, setMsalReady] = useState(false);
    const msalInstanceRef = useRef(null);

    const handleMicrosoftLogin = useCallback((error, data) => {
        setIsButtonClicked(true);

        if (error) {
            console.error('Microsoft login error:', error);
            setTimeout(() => setIsButtonClicked(false), 500);
            return;
        }

        const idToken = data?.idToken;

        if (!(idToken && fetchMicrosoftLogin)) {
            console.error('Microsoft login: idToken not found or fetchMicrosoftLogin not available');
            setTimeout(() => setIsButtonClicked(false), 500);
            return;
        }

        fetchMicrosoftLogin({
            data: { idToken },
            apiId: 'microsoftloginform',
            callback: (out) => {
                setTimeout(() => setIsButtonClicked(false), 500);
                if (!out || out === 'error' || out === null) {
                    setTimeout(() => setIsButtonClicked(false), 500);
                    return;
                }
                if (out === 'mfaEnabled') {
                    history.push('/user/mfa');
                    return;
                }
                let redirectUrl = '/';
                if (window.$environment.SERVICE_PROVIDER === 'eldx') {
                    redirectUrl = window.$environment.RESELLER;
                }
                if (redirectedFromFleetops) {
                    const redirectionURL = new URL(redirectedFromFleetops);
                    redirectionURL.searchParams.set('token', out.token);
                    redirectionURL.searchParams.set('partnerId', window.$environment?.DOMAIN);
                    redirectUrl = redirectionURL.toString();
                } else if (redirectedUrl) {
                    redirectUrl = redirectedUrl;
                }
                window.location.href = redirectUrl;
            },
        });
    }, [fetchMicrosoftLogin, history, redirectedFromFleetops, redirectedUrl]);

    const clientId = microsoftClientId || window.$environment?.MICROSOFT_CLIENT_ID;
    const isSsoEnabled = webAppConfigurations?.settings?.MICROSOFT_SSO || false;
    const isLoading = isButtonClicked || hasMicrosoftLoginApiStatus?.isLoading || false;

    useEffect(() => {
        if (!clientId || !isSsoEnabled) {
            return;
        }

        let cancelled = false;
        const msalConfig = {
            auth: {
                clientId,
                redirectUri: window.location.origin,
                navigateToLoginRequestUrl: false,
            },
            cache: {
                cacheLocation: 'localStorage',
            },
        };

        const initAndHandleRedirect = async () => {
            try {
                const msalInstance = new PublicClientApplication(msalConfig);
                if (typeof msalInstance.initialize === 'function') {
                    await msalInstance.initialize();
                }
                if (cancelled) return;

                msalInstanceRef.current = msalInstance;

                const result = await msalInstance.handleRedirectPromise();
                if (cancelled) return;

                if (result && result.idToken) {
                    handleMicrosoftLogin(null, { idToken: result.idToken });
                    return;
                }

                setMsalReady(true);
            } catch (error) {
                if (!cancelled) {
                    console.error('Microsoft SSO init error:', error);
                    handleMicrosoftLogin(error, null);
                    setMsalReady(true);
                }
            }
        };

        initAndHandleRedirect();
        return () => { cancelled = true; };
    }, [clientId, isSsoEnabled, handleMicrosoftLogin]);

    const handleButtonClick = async () => {
        const msalInstance = msalInstanceRef.current;
        if (!msalInstance) return;

        setIsButtonClicked(true);
        try {
            const result = await msalInstance.loginPopup({
                scopes: ['openid', 'profile'],
                prompt: 'select_account',
            });
            if (result?.idToken) {
                handleMicrosoftLogin(null, { idToken: result.idToken });
            } else {
                handleMicrosoftLogin(new Error('No idToken in response'), null);
            }
        } catch (error) {
            console.error('Microsoft login popup error:', error);
            handleMicrosoftLogin(error, null);
            setIsButtonClicked(false);
        }
    };

    if (!clientId || !isSsoEnabled) {
        return null;
    }

    return (
        <Styled.MicrosoftLoginWrapper layout={layout} data-testid="microsoft-login-wrapper">
            <Styled.MicrosoftDivider>
                <Styled.DividerLine sp={serviceProvider} />
                <Styled.DividerText sp={serviceProvider}>{i18n.t('OR')}</Styled.DividerText>
                <Styled.DividerLine sp={serviceProvider} />
            </Styled.MicrosoftDivider>

            {isLoading ? (
                <Styled.MicrosoftLoginLoadingWrapper>
                    <Styled.MicrosoftIconBox>
                        <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="0" y="0" width="10" height="10" fill="#F25022" />
                            <rect x="11" y="0" width="10" height="10" fill="#7FBA00" />
                            <rect x="0" y="11" width="10" height="10" fill="#00A4EF" />
                            <rect x="11" y="11" width="10" height="10" fill="#FFB900" />
                        </svg>
                    </Styled.MicrosoftIconBox>
                    <CircularProgress color="inherit" size={24} />
                    <span style={{ marginLeft: '8px', color: 'black' }}>{i18n.t('Signing in...')}</span>
                </Styled.MicrosoftLoginLoadingWrapper>
            ) : (
                <Styled.MicrosoftLoginLoadingWrapper
                    as="button"
                    type="button"
                    onClick={msalReady ? handleButtonClick : undefined}
                    disabled={!msalReady}
                    style={{ cursor: msalReady ? 'pointer' : 'wait', border: 'none' }}
                >
                    <Styled.MicrosoftIconBox>
                        <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="0" y="0" width="10" height="10" fill="#F25022" />
                            <rect x="11" y="0" width="10" height="10" fill="#7FBA00" />
                            <rect x="0" y="11" width="10" height="10" fill="#00A4EF" />
                            <rect x="11" y="11" width="10" height="10" fill="#FFB900" />
                        </svg>
                    </Styled.MicrosoftIconBox>
                    <span style={{ marginLeft: '8px', color: 'black' }}>
                        {msalReady ? i18n.t('Sign in with Microsoft') : i18n.t('Loading...')}
                    </span>
                </Styled.MicrosoftLoginLoadingWrapper>
            )}
        </Styled.MicrosoftLoginWrapper>
    );
};

MicrosoftLoginButton.propTypes = {
    history: PropTypes.object.isRequired,
    fetchMicrosoftLogin: PropTypes.func.isRequired,
    i18n: PropTypes.object.isRequired,
    Styled: PropTypes.object.isRequired,
    webAppConfigurations: PropTypes.object,
    hasMicrosoftLoginApiStatus: PropTypes.object,
};

export default MicrosoftLoginButton;
